"use client";

import { IconMagnifyingGlass } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import { getChainIconUrl } from "@/app/api/chains/chainIcons";
import { FilterSidebar } from "@/components/ui/filter-sidebar";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { networkToChainId } from "@/components/x402/X402ImportWizardUtils";
import type { X402Endpoint } from "@/hooks/useX402Endpoints";
import { providerDisplayName } from "@/lib/x402/providerSlug";
import { groupEndpointsByProvider } from "@/lib/x402/groupEndpointsByProvider";
import Link from "next/link";
import { useMemo, useState } from "react";
import { formatNetworkLabel } from "./NetworkChip";
import { ProviderCard } from "./ProviderCard";
interface Props {
  endpoints: X402Endpoint[];
}
const ALL_NETWORKS = "All networks";
export function ProviderGrid({
  endpoints
}: Props) {
  const [search, setSearch] = useState("");
  const [network, setNetwork] = useState<string>(ALL_NETWORKS);
  const [source, setSource] = useState<"all" | "cdp_bazaar" | "user_import">("all");
  const networksWithCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const e of endpoints) counts.set(e.network, (counts.get(e.network) ?? 0) + 1);
    return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [endpoints]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return endpoints.filter(e_0 => {
      if (network !== ALL_NETWORKS && e_0.network !== network) return false;
      if (source !== "all" && e_0.source !== source) return false;
      if (!q) return true;
      return e_0.name.toLowerCase().includes(q) || e_0.baseUrl.toLowerCase().includes(q) || (e_0.description ?? "").toLowerCase().includes(q);
    });
  }, [endpoints, search, network, source]);
  const grouped = useMemo(() => groupEndpointsByProvider(filtered), [filtered]);
  const providers = useMemo(() => {
    return Object.entries(grouped).map(([slug, list]) => ({
      slug,
      apex: list[0] ? providerDisplayName(list[0].baseUrl) : slug.replace(/-/g, "."),
      endpoints: list,
      totalCalls: list.reduce((sum, e_1) => sum + (e_1.totalCalls ?? 0), 0)
    })).sort((a_0, b_0) => b_0.endpoints.length - a_0.endpoints.length || b_0.totalCalls - a_0.totalCalls);
  }, [grouped]);
  const networkItems = useMemo(() => [{
    key: ALL_NETWORKS,
    label: ALL_NETWORKS,
    count: endpoints.length
  }, ...networksWithCounts.map(([n, count]) => {
    const chainId = networkToChainId(n);
    return {
      key: n,
      label: formatNetworkLabel(n),
      count,
      iconUrl: chainId != null ? getChainIconUrl(chainId) : null
    };
  })], [endpoints.length, networksWithCounts]);
  const sourceItems = useMemo(() => [{
    key: "all",
    label: "All sources"
  }, {
    key: "cdp_bazaar",
    label: "CDP Bazaar"
  }, {
    key: "user_import",
    label: "Community import"
  }], []);
  return <div className="flex w-full gap-8">
      <FilterSidebar groups={[{
      kind: "select",
      items: networkItems,
      activeKey: network,
      onChange: setNetwork
    }, {
      kind: "select",
      items: sourceItems,
      activeKey: source,
      onChange: key => setSource(key as "all" | "cdp_bazaar" | "user_import")
    }]} />

      <div className="flex min-w-0 flex-1 flex-col gap-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-5">
          <Tabs value="x402" className="min-w-0 flex-1">
            <TabsList className="w-full justify-start overflow-x-auto sm:w-auto">
              <TabsTrigger value="all" asChild>
                <Link href="/integrations">All integrations</Link>
              </TabsTrigger>
              <TabsTrigger value="x402" count={endpoints.length}>
                x402 Endpoints
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="rounded-ds-md border-ds-border bg-ds-surface-sunken shadow-ds-edge-light-debossed relative flex h-[44px] w-full items-center gap-2 border py-[10px] pl-[10px] pr-2 sm:max-w-[400px] sm:shrink-0">
            <IconMagnifyingGlass className="text-ds-text-disabled h-5 w-5 shrink-0" />
            <Input type="text" placeholder="Search endpoints + providers..." value={search} onChange={e_2 => setSearch(e_2.target.value)} className="text-ds-text-primary placeholder:text-ds-text-disabled leading-label h-5 flex-1 border-0 bg-transparent p-0 text-sm shadow-none focus-visible:ring-0 focus-visible:ring-offset-0" />
          </div>
        </div>

        {providers.length === 0 ? <div className="text-ds-text-muted py-20 text-center text-sm">No endpoints match your filters.</div> : <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {providers.map(p => <ProviderCard key={p.slug} apex={p.apex} providerSlug={p.slug} endpoints={p.endpoints} />)}
          </div>}
      </div>
    </div>;
}